import type { NotamRow } from "../api";
import { escapeHtml } from "../escape";
import { annotateBody, decodeQCode, translateBody } from "../notam-lang";
import { $notams } from "../store";

const SEVERITY_JA: Record<"crit" | "warn" | "info", { label: string; icon: string }> = {
  crit: { label: "重大", icon: "ph ph-warning-octagon-fill" },
  warn: { label: "注意", icon: "ph ph-warning-fill" },
  info: { label: "情報", icon: "ph ph-info" },
};

/**
 * NOTAM panel — toggled from the header NOTAM badge. Lists active NOTAMs
 * grouped by severity (crit → warn → info). Q-codes and common
 * NOTAM abbreviations get Japanese tooltips so non-aviation users can
 * actually parse them.
 */
export function renderNotamPanel(host: HTMLElement) {
  host.innerHTML = `
    <div class="notam-panel card" id="notamPanel" aria-hidden="true">
      <div class="head">
        <div class="l">
          <i class="ph ph-warning-circle" style="color:var(--accent-amber)"></i>
          <span class="title">NOTAM (運航情報)</span>
          <span class="sub" id="notamCount">0 件</span>
        </div>
        <div class="r">
          <button class="iconbtn" id="notamClose" aria-label="閉じる"><i class="ph ph-x"></i></button>
        </div>
      </div>
      <div class="divider"></div>
      <div class="notam-list scroll" id="notamList"></div>
    </div>
  `;

  const panel = host.querySelector<HTMLElement>("#notamPanel")!;
  const list = host.querySelector<HTMLElement>("#notamList")!;
  const count = host.querySelector<HTMLElement>("#notamCount")!;
  host.querySelector("#notamClose")?.addEventListener("click", () => setOpen(false));

  function setOpen(open: boolean) {
    panel.classList.toggle("open", open);
    panel.setAttribute("aria-hidden", String(!open));
  }

  document.addEventListener("pifeeder:toggle-notams", () => setOpen(!panel.classList.contains("open")));
  document.addEventListener("pifeeder:show-notams", () => setOpen(true));
  // Scroll a specific NOTAM into view when its map marker is clicked. The
  // row carries data-notam-id so we can find it without a separate index.
  document.addEventListener("pifeeder:focus-notam", (e) => {
    const id = (e as CustomEvent<{ id: string }>).detail?.id;
    if (!id) return;
    setOpen(true);
    requestAnimationFrame(() => {
      const row = list.querySelector<HTMLElement>(`[data-notam-id="${CSS.escape(id)}"]`);
      if (!row) return;
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      row.classList.add("notam-flash");
      setTimeout(() => row.classList.remove("notam-flash"), 1500);
    });
  });

  function fmtPeriod(from: string | null, to: string | null) {
    if (!from && !to) return "発令中 (期限なし)";
    const dt = (s: string) =>
      new Date(s).toLocaleString("ja-JP", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    const fromStr = from ? dt(from) : "—";
    const toStr = to ? dt(to) : "期限なし";
    return `${fromStr} 〜 ${toStr}`;
  }

  function renderRow(n: NotamRow): string {
    // Guard sevKey against an unexpected/hostile `severity` value: keep it to
    // the known set so it is safe inside `class="sev-…"` AND so SEVERITY_JA
    // lookup can't return undefined (which previously threw on sev.icon).
    const sevKey = (
      ["crit", "warn", "info"].includes(n.severity ?? "") ? n.severity : "info"
    ) as keyof typeof SEVERITY_JA;
    const sev = SEVERITY_JA[sevKey];
    const q = decodeQCode(n.notamCode);
    const qLabel = q ? `${q.subject} ${q.status}` : "";
    const summary = translateBody(n.body || "");
    // translateBody returns plain text (no intentional HTML), so escape its
    // whole output. Every other interpolated field is upstream-controlled
    // NOTAM data and is escaped for both element-body and attribute contexts.
    const codeTitle = q ? `${q.raw}: ${q.subject} / ${q.status}` : (n.notamCode ?? "");
    return `
      <div class="notam-row sev-${sevKey}" data-notam-id="${escapeHtml(n.notamId)}">
        <div class="notam-head">
          <span class="notam-sev"><i class="${sev.icon}"></i>${sev.label}</span>
          ${
            n.notamCode
              ? `<span class="notam-code" title="${escapeHtml(codeTitle)}">${escapeHtml(n.notamCode)}</span>`
              : ""
          }
          ${qLabel ? `<span class="notam-qmeaning">${escapeHtml(qLabel)}</span>` : ""}
          <span class="notam-id">${escapeHtml(n.notamId.slice(0, 8))}…</span>
        </div>
        ${summary ? `<div class="notam-summary">${escapeHtml(summary)}</div>` : ""}
        ${n.body?.trim() ? `<div class="notam-body">${annotateBody(n.body).slice(0, 1200)}</div>` : ""}
        <div class="notam-period">${fmtPeriod(n.validFrom, n.validTo)}</div>
      </div>`;
  }

  function render(rows: readonly NotamRow[]) {
    // Skip body-less NOTAMs entirely. The Pi/API occasionally emits
    // metadata-only rows (validity window + Q-code, no narrative text),
    // which produced "(本文なし)" placeholder cards that just added noise.
    const visible = rows.filter((n) => n.body?.trim());
    count.textContent = `${visible.length} 件`;
    if (visible.length === 0) {
      list.innerHTML = `<div class="empty">現在発令中の NOTAM はありません。</div>`;
      return;
    }
    list.innerHTML = visible.map(renderRow).join("");
  }

  $notams.subscribe(render);
}
